import { useCallback, useEffect, useState } from "react";
import { KeyRound, Pencil, Plus, ShieldCheck, Trash2, UserX } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";

const ROLE_BADGE = {
    superadmin: "bg-cherry/10 text-cherry",
    admin: "bg-brand-mist text-brand",
};

function fmtDate(iso) {
    try {
        return new Date(iso).toLocaleDateString("id-ID", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso || "";
    }
}

export default function AdminsPanel({ me, toast, onAuthUpdate }) {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [addForm, setAddForm] = useState({ name: "", email: "", password: "", role: "admin" });
    const [saving, setSaving] = useState(false);
    const [pwTarget, setPwTarget] = useState(null);
    const [pwForm, setPwForm] = useState({ new_password: "", confirm: "" });
    const [editTarget, setEditTarget] = useState(null);
    const [editForm, setEditForm] = useState({ name: "", email: "", role: "admin" });

    const load = useCallback(async () => {
        try {
            const res = await api.get("/admin/admins");
            setAdmins(res.data);
        } catch (err) {
            toast({
                title: "Gagal memuat daftar admin",
                description: formatApiError(err),
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        load();
    }, [load]);

    const saveNew = async () => {
        if (addForm.name.trim().length < 2 || addForm.password.length < 8) {
            toast({
                title: "Lengkapi data dengan benar",
                description: "Nama minimal 2 karakter, password minimal 8 karakter.",
                variant: "destructive",
            });
            return;
        }
        setSaving(true);
        try {
            await api.post("/admin/admins", {
                name: addForm.name.trim(),
                email: addForm.email.trim(),
                password: addForm.password,
                role: addForm.role,
            });
            toast({ title: `Admin baru ditambahkan: ${addForm.name.trim()}` });
            setAddForm({ name: "", email: "", password: "", role: "admin" });
            setShowAdd(false);
            load();
        } catch (err) {
            toast({
                title: "Gagal menambahkan admin",
                description: formatApiError(err),
                variant: "destructive",
            });
        } finally {
            setSaving(false);
        }
    };

    const savePassword = async () => {
        if (pwForm.new_password.length < 8 || pwForm.new_password !== pwForm.confirm) {
            toast({
                title: "Password tidak valid",
                description: "Minimal 8 karakter dan konfirmasi harus sama.",
                variant: "destructive",
            });
            return;
        }
        try {
            await api.patch(`/admin/admins/${pwTarget.id}/password`, {
                old_password: pwTarget.self ? pwForm.old_password : undefined,
                new_password: pwForm.new_password,
            });
            toast({ title: `Password ${pwTarget.name} diperbarui` });
            setPwTarget(null);
            setPwForm({ new_password: "", confirm: "" });
        } catch (err) {
            toast({
                title: "Gagal mengganti password",
                description: formatApiError(err),
                variant: "destructive",
            });
        }
    };

    const saveEdit = async () => {
        try {
            const payload = { name: editForm.name.trim(), role: editForm.role };
            if (editForm.email.trim().toLowerCase() !== editTarget.email) {
                payload.email = editForm.email.trim();
            }
            const res = await api.patch(`/admin/admins/${editTarget.id}`, payload);
            if (res.data?.token) {
                sessionStorage.setItem("dk_admin_token", res.data.token);
                sessionStorage.setItem("dk_admin_info", JSON.stringify(res.data));
                onAuthUpdate?.(res.data.token, res.data);
            }
            toast({ title: "Data admin diperbarui" });
            setEditTarget(null);
            load();
        } catch (err) {
            toast({
                title: "Gagal memperbarui admin",
                description: formatApiError(err),
                variant: "destructive",
            });
        }
    };

    const toggleActive = async (a) => {
        try {
            await api.patch(`/admin/admins/${a.id}`, { is_active: !a.is_active });
            toast({ title: a.is_active ? "Admin dinonaktifkan" : "Admin diaktifkan" });
            load();
        } catch (err) {
            toast({
                title: "Gagal mengubah status",
                description: formatApiError(err),
                variant: "destructive",
            });
        }
    };

    const remove = async (a) => {
        if (!window.confirm(`Hapus admin ${a.name} (${a.email})?`)) return;
        try {
            await api.delete(`/admin/admins/${a.id}`);
            toast({ title: "Admin dihapus" });
            load();
        } catch (err) {
            toast({
                title: "Gagal menghapus admin",
                description: formatApiError(err),
                variant: "destructive",
            });
        }
    };

    return (
        <div data-testid="admins-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-ink/50">
                    Kelola akun admin yang dapat login ke dashboard ini.
                </p>
                <Button
                    data-testid="admin-add-button"
                    onClick={() => setShowAdd((v) => !v)}
                    className="h-11 rounded-full bg-brand px-6 font-bold text-white hover:bg-brand-deep"
                >
                    <Plus size={17} className="mr-1.5" />
                    Tambah Admin
                </Button>
            </div>

            {showAdd && (
                <div
                    data-testid="admin-add-form"
                    className="mt-5 rounded-3xl border border-border bg-white p-8 shadow-brand"
                >
                    <h3 className="font-display text-xl font-extrabold text-ink">
                        Tambah Admin Baru
                    </h3>
                    <div className="mt-6 grid gap-5 sm:grid-cols-2">
                        <div className="space-y-2">
                            <Label className="text-sm font-bold text-ink/80">Nama</Label>
                            <Input
                                data-testid="admin-add-name"
                                value={addForm.name}
                                onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                                placeholder="Nama admin"
                                className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-bold text-ink/80">Email</Label>
                            <Input
                                data-testid="admin-add-email"
                                type="email"
                                value={addForm.email}
                                onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                                placeholder="admin2@digiklin.id"
                                className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-bold text-ink/80">
                                Password <span className="font-medium text-ink/40">(minimal 8 karakter)</span>
                            </Label>
                            <Input
                                data-testid="admin-add-password"
                                type="password"
                                value={addForm.password}
                                onChange={(e) => setAddForm((f) => ({ ...f, password: e.target.value }))}
                                placeholder="••••••••"
                                className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-bold text-ink/80">Role</Label>
                            <Select value={addForm.role} onValueChange={(v) => setAddForm((f) => ({ ...f, role: v }))}>
                                <SelectTrigger
                                    data-testid="admin-add-role"
                                    className="h-12 w-full rounded-xl border-ink/10 bg-[#F9FAFB] focus:ring-brand"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="admin">Admin (kelola pesanan & konten)</SelectItem>
                                    <SelectItem value="superadmin">Superadmin (+ kelola admin)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="mt-6 flex gap-3">
                        <Button
                            data-testid="admin-add-save"
                            onClick={saveNew}
                            disabled={saving}
                            className="h-11 rounded-full bg-brand px-8 font-bold text-white hover:bg-brand-deep disabled:opacity-60"
                        >
                            {saving ? "Menyimpan..." : "Simpan Admin"}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setShowAdd(false)}
                            className="h-11 rounded-full border-ink/10 font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                        >
                            Batal
                        </Button>
                    </div>
                </div>
            )}

            <div className="mt-6 overflow-hidden rounded-3xl border border-border bg-white shadow-brand">
                <div className="overflow-x-auto" data-testid="admins-table">
                    {loading ? (
                        <p className="py-14 text-center text-sm text-ink/50">Memuat...</p>
                    ) : (
                        <div className="divide-y divide-border">
                            {admins.map((a) => (
                                <div
                                    key={a.id}
                                    data-testid={`admins-row-${a.email}`}
                                    className="flex flex-wrap items-center justify-between gap-4 px-6 py-5 sm:px-8"
                                >
                                    <div className="min-w-0">
                                        <p className="flex flex-wrap items-center gap-2 font-bold text-ink">
                                            {a.name || a.email}
                                            <span
                                                className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${ROLE_BADGE[a.role]}`}
                                                data-testid={`admins-role-${a.email}`}
                                            >
                                                {a.role === "superadmin" ? "Superadmin" : "Admin"}
                                            </span>
                                            {!a.is_active && (
                                                <span className="rounded-full bg-slate-200 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600">
                                                    Nonaktif
                                                </span>
                                            )}
                                        </p>
                                        <p className="mt-0.5 text-sm text-ink/50">
                                            {a.email} · bergabung {fmtDate(a.created_at)}
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <Button
                                            data-testid={`admins-pw-${a.email}`}
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                                setPwTarget({ ...a, self: a.id === me.id });
                                                setPwForm({ new_password: "", confirm: "" });
                                            }}
                                            className="rounded-full border-ink/10 text-xs font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                                        >
                                            <KeyRound size={13} className="mr-1" />
                                            Ganti Password
                                        </Button>
                                        <Button
                                            data-testid={`admins-edit-${a.email}`}
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                                setEditTarget(a);
                                                setEditForm({ name: a.name, email: a.email, role: a.role });
                                            }}
                                            className="rounded-full border-ink/10 text-xs font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                                        >
                                            <Pencil size={13} className="mr-1" />
                                            Ubah
                                        </Button>
                                        {a.id !== me.id && (
                                            <>
                                                <Button
                                                    data-testid={`admins-toggle-${a.email}`}
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => toggleActive(a)}
                                                    className="rounded-full border-ink/10 text-xs font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                                                >
                                                    {a.is_active ? "Nonaktifkan" : "Aktifkan"}
                                                </Button>
                                                <Button
                                                    data-testid={`admins-delete-${a.email}`}
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => remove(a)}
                                                    className="rounded-full border-ink/10 text-xs font-bold text-ink hover:bg-red-50 hover:text-red-600"
                                                >
                                                    <Trash2 size={13} className="mr-1" />
                                                    Hapus
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Dialog ganti password */}
            <Dialog open={!!pwTarget} onOpenChange={(o) => !o && setPwTarget(null)}>
                <DialogContent data-testid="password-dialog" className="rounded-3xl sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl font-extrabold text-ink">
                            Ganti Password — {pwTarget?.name}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        {pwTarget?.self && (
                            <div className="space-y-2">
                                <Label className="text-sm font-bold text-ink/80">Password Lama</Label>
                                <Input
                                    data-testid="pw-old"
                                    type="password"
                                    value={pwForm.old_password || ""}
                                    onChange={(e) => setPwForm((f) => ({ ...f, old_password: e.target.value }))}
                                    className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                                />
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label className="text-sm font-bold text-ink/80">
                                Password Baru <span className="font-medium text-ink/40">(minimal 8 karakter)</span>
                            </Label>
                            <Input
                                data-testid="pw-new"
                                type="password"
                                value={pwForm.new_password}
                                onChange={(e) => setPwForm((f) => ({ ...f, new_password: e.target.value }))}
                                className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-bold text-ink/80">Konfirmasi Password Baru</Label>
                            <Input
                                data-testid="pw-confirm"
                                type="password"
                                value={pwForm.confirm}
                                onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))}
                                className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                            />
                        </div>
                        <p className="flex items-start gap-2 rounded-xl bg-brand-mist/60 px-4 py-3 text-xs leading-relaxed text-ink/60">
                            <ShieldCheck size={14} className="mt-0.5 shrink-0 text-brand" />
                            {pwTarget?.self
                                ? "Password lama wajib diisi untuk mengganti password Anda sendiri."
                                : "Sebagai superadmin, Anda dapat mengatur password baru tanpa password lama."}
                        </p>
                    </div>
                    <div className="flex gap-3 pt-2">
                        <Button
                            data-testid="pw-save"
                            onClick={savePassword}
                            className="h-11 flex-1 rounded-full bg-brand font-bold text-white hover:bg-brand-deep"
                        >
                            Simpan Password
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setPwTarget(null)}
                            className="h-11 rounded-full border-ink/10 font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                        >
                            Batal
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog ubah admin */}
            <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
                <DialogContent data-testid="edit-admin-dialog" className="rounded-3xl sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl font-extrabold text-ink">
                            Ubah Admin
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label className="text-sm font-bold text-ink/80">Nama</Label>
                            <Input
                                data-testid="edit-admin-name"
                                value={editForm.name}
                                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                                className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-bold text-ink/80">Email Login</Label>
                            <Input
                                data-testid="edit-admin-email"
                                type="email"
                                value={editForm.email}
                                onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                                className="h-12 rounded-xl border-ink/10 bg-[#F9FAFB] focus-visible:ring-brand"
                            />
                            <p className="text-xs text-ink/40">
                                Email ini dipakai untuk login. Jika mengubah email
                                akun Anda sendiri, sesi tetap aktif.
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-bold text-ink/80">Role</Label>
                            <Select value={editForm.role} onValueChange={(v) => setEditForm((f) => ({ ...f, role: v }))}>
                                <SelectTrigger
                                    data-testid="edit-admin-role"
                                    className="h-12 w-full rounded-xl border-ink/10 bg-[#F9FAFB] focus:ring-brand"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="admin">Admin</SelectItem>
                                    <SelectItem value="superadmin">Superadmin</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <p className="flex items-start gap-2 rounded-xl bg-brand-mist/60 px-4 py-3 text-xs leading-relaxed text-ink/60">
                            <UserX size={14} className="mt-0.5 shrink-0 text-brand" />
                            Menonaktifkan admin tidak menghapus datanya — akun bisa diaktifkan kembali.
                        </p>
                    </div>
                    <div className="flex gap-3 pt-2">
                        <Button
                            data-testid="edit-admin-save"
                            onClick={saveEdit}
                            className="h-11 flex-1 rounded-full bg-brand font-bold text-white hover:bg-brand-deep"
                        >
                            Simpan
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setEditTarget(null)}
                            className="h-11 rounded-full border-ink/10 font-bold text-ink hover:bg-brand-mist/50 hover:text-brand"
                        >
                            Batal
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
